/**
 * Hediye ürün seçim pop-up'ı.
 *
 * Akış:
 *  1. Kampanyaya dahil bir ürün için "Sepete Ekle"ye basılır.
 *  2. Ürün HENÜZ sepete gitmeden pop-up anında açılır (sepet sorgusu beklenmez).
 *  3. Hediye seçilince hediye ayrı bir istekle sepete eklenir.
 *  4. "Hediye istemiyorum" denirse sadece ürün eklenir.
 *
 * Güvenceler:
 *  - Kampanya kapalıysa snippet render edilmez, bu dosya devreye girmez.
 *  - Hediye, sepetteki otomatik indirim TOPLAMI hediye tutarını karşılıyorsa
 *    geçerli sayılır. (Shopify "Satın al X kazan Y" indirimini her zaman bizim
 *    eklediğimiz satıra düşürmez; aynı üründen iki satır varsa indirimi
 *    ucuz/ilk satıra uygulayabilir. Sadece satır fiyatına bakmak, yeni eklenen
 *    hediyenin haksız yere sepetten silinmesine yol açıyordu.)
 *  - Kampanya ürünü sepetten çıkarılırsa onunla gelen hediye de çıkarılır.
 *  - Mini çantalar hem tetikleyici hem hediye koleksiyonunda; "2 Al 1 Öde"
 *    indirimini hangi mini çanta satırına düşüreceğine Shopify karar veriyor
 *    ve bu temadan yönlendirilemiyor (satırı değiştirmek indirimi kaydırıyor).
 *    Bu yüzden hediye etiketi GİZLİ özellikte tutulur (_hediye) ve sepette
 *    "HEDİYE" çerçevesi, gerçekten 0 TL'ye düşen mini çanta satırına çizilir.
 *    Böylece "hediye" yazan satır hiçbir zaman fiyatlı görünmez.
 *  - Tetikleyici ürün/varyant listesi Liquid'de 50 ürünle sınırlı olduğu için
 *    koleksiyonun tamamı products.json ile tamamlanır (oturum boyu önbellekli).
 */

/** "_" ile başlayan özellikler sepette ve ödeme sayfasında müşteriye gösterilmez. */
const GIFT_PROP = '_hediye';
/** Önceki sürümün görünür özelliği — sepette hâlâ bu şekilde duran hediyeler için. */
const LEGACY_GIFT_PROP = 'Hediye';
/** Hediye varyant dropdown'ının etiketi. Ürünün option adı ne olursa olsun bu kullanılır. */
const OPTION_LABEL = 'Askı Rengi';
const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
/** Otomatik indirimin hediye satırına işlemesi için tanınan süre (ms). */
const DISCOUNT_GRACE_MS = 6000;
const CACHE_KEY_PREFIX = 'giftPickerEligible:v2:';
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** İndirim kodu ile gelen indirimler hediye sayılmaz; yalnızca otomatik indirim. */
function automaticDiscountTotal(item) {
  const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
  let total = 0;

  for (const allocation of allocations) {
    const application = allocation && allocation.discount_application;
    if (application && application.type === 'discount_code') continue;
    total += Number(allocation && allocation.amount) || 0;
  }

  return total;
}

class GiftPickerComponent extends HTMLElement {
  #config = { eligible: [], eligibleVariants: [], gifts: [] };
  #eligibleIds = new Set();
  #eligibleVariantIds = new Set();
  #selected = null;
  #adding = false;
  #checking = false;
  #giftsFree = false;
  #lastState = 0;
  #limitReached = false;
  #limitAtQty = 0;
  #eligibleQty = 0;
  #giftQty = 0;
  #autoOpenSuppressed = false;
  #giftAddedAt = 0;
  /** Sepette "HEDİYE" çerçevesi çizilecek satır anahtarları (gerçekten 0 TL olanlar). */
  #giftLineKeys = [];
  /** [hediye satırı, alınan satır] çiftleri — sepette fiyat alanları yer değiştirir. */
  #priceSwaps = [];
  /** Hediye eklendikten sonra indirimi hediyeye taşımak için kalan deneme hakkı. */
  #nudgeBudget = 0;

  connectedCallback() {
    try {
      const configEl = this.querySelector('[data-gift-config]');
      this.#config = JSON.parse((configEl && configEl.textContent) || '{}');
    } catch (error) {
      console.warn('[gift-picker] config parse error', error);
      return;
    }

    this.#eligibleIds = new Set((this.#config.eligible || []).map(Number).filter(Boolean));
    this.#eligibleVariantIds = new Set((this.#config.eligibleVariants || []).map(Number).filter(Boolean));

    this.dialog = this.querySelector('dialog');
    this.mainImage = this.querySelector('[data-gift-main-image]');
    this.titleEl = this.querySelector('[data-gift-title]');
    this.compareEl = this.querySelector('[data-gift-compare]');
    this.submitButton = this.querySelector('[data-gift-submit]');
    this.submitText = this.querySelector('[data-gift-submit-text]');
    this.variantField = this.querySelector('[data-gift-field]');
    this.variantSelect = this.querySelector('[data-gift-variant]');
    this.variantLabel = this.querySelector('[data-gift-option-label]');
    this.variantError = this.querySelector('[data-gift-error]');
    this.addError = this.querySelector('[data-gift-add-error]');

    const firstOption = this.querySelector('[data-gift-option]:not([disabled])');
    if (firstOption) this.#select(firstOption);

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', () => {
        this.#selected = this.variantSelect.value || null;
        if (this.variantSelect.value) this.#showVariantError(false);
      });
    }

    this.addEventListener('click', this.#handleClick);
    if (this.dialog) this.dialog.addEventListener('cancel', () => this.#finish(false));

    // Sepete ekleme formunu yakala — pop-up bekletmeden açılsın diye
    // capture aşamasında dinleniyor.
    document.addEventListener('submit', this.#handleSubmit, true);

    window.__giftPicker = this;
    installCartHook();

    console.info(
      '[gift-picker] hazır —',
      'tetikleyici ürün:', this.#eligibleIds.size,
      '| tetikleyici varyant:', this.#eligibleVariantIds.size,
      '| hediye ürün:', (this.#config.gifts || []).length,
      '| hediye koleksiyonu:', this.#config.giftCollection || '(bulunamadı)'
    );

    // Liquid 50 ürünle sınırlı; koleksiyonun tamamını arka planda tamamla.
    this.#completeEligible();

    this.#watchCart();
    this.#readCart().then(() => this.markGiftLines());

    if (window.location.search.includes('giftdebug=1')) this.forceOpen();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.#handleSubmit, true);
  }

  get busy() {
    return this.#adding || this.#checking;
  }

  forceOpen() {
    if (this.dialog && !this.dialog.open) this.dialog.showModal();
  }

  /* ---------- Tetikleyici ürün/varyant listesini tamamlama ---------- */

  #merge(productIds, variantIds) {
    for (const id of productIds || []) {
      const numeric = Number(id);
      if (numeric) this.#eligibleIds.add(numeric);
    }
    for (const id of variantIds || []) {
      const numeric = Number(id);
      if (numeric) this.#eligibleVariantIds.add(numeric);
    }
  }

  /**
   * Koleksiyondaki TÜM ürünleri ve varyantlarını products.json ile çeker.
   * Liquid tarafı bir koleksiyondan en fazla 50 ürün döndürebildiği için
   * 50'den büyük koleksiyonlarda pop-up bazı ürünlerde hiç açılmıyordu.
   */
  async #completeEligible() {
    const handles = (this.#config.eligibleCollections || []).map((h) => String(h).trim()).filter(Boolean);
    if (!handles.length) return;

    const cacheKey = CACHE_KEY_PREFIX + handles.join(',');

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        this.#merge(data.products, data.variants);
        return;
      }
    } catch (error) {
      /* sessionStorage kapalı olabilir — yok say */
    }

    const productIds = [];
    const variantIds = [];

    for (const handle of handles) {
      for (let page = 1; page <= 8; page += 1) {
        try {
          const response = await fetch(
            `${ROOT()}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`
          );
          if (!response.ok) break;

          const data = await response.json();
          const products = (data && data.products) || [];

          for (const product of products) {
            productIds.push(product.id);
            for (const variant of product.variants || []) variantIds.push(variant.id);
          }

          if (products.length < 250) break;
        } catch (error) {
          console.warn('[gift-picker] koleksiyon ürünleri okunamadı:', handle, error);
          break;
        }
      }
    }

    if (!productIds.length) return;

    this.#merge(productIds, variantIds);

    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ products: Array.from(this.#eligibleIds), variants: Array.from(this.#eligibleVariantIds) })
      );
    } catch (error) {
      /* yok say */
    }
  }

  /* ---------- Sepete ekleme yakalama ---------- */

  #isEligibleVariant(variantId) {
    const numeric = Number(variantId);
    return !!numeric && this.#eligibleVariantIds.has(numeric);
  }

  #isEligibleProduct(productId) {
    const numeric = Number(productId);
    return !!numeric && this.#eligibleIds.has(numeric);
  }

  /**
   * Pop-up'ı açmaya çalışır. Sepet sorgulanmaz — önbellekteki duruma bakılır,
   * böylece pop-up tıklama ile aynı karede açılır.
   */
  openFor(quantity) {
    try {
      if (!this.dialog || this.dialog.open) return false;
      if (this.#adding) return false;
      if (!this.#config.gifts || !this.#config.gifts.length) return false;

      const qty = Number(quantity) || 1;

      if (this.#limitReached && this.#eligibleQty + qty <= this.#limitAtQty) return false;
      if (this.#eligibleQty + qty <= this.#giftQty) return false;

      // Gönderim ENGELLENMEZ: ürünü tema kendi akışıyla ekler (çift ekleme olmaz).
      // Biz yalnızca cart drawer'ın kendiliğinden açılmasını erteleriz.
      this.#suppressDrawerAutoOpen();
      this.#closeQuickAdd();
      this.#showAddError('');
      this.dialog.showModal();
      return true;
    } catch (error) {
      return false;
    }
  }

  /** fetch hook'undan çağrılır: /cart/add isteği gönderilmeden ÖNCE. */
  notifyAdd(variantIds, quantity) {
    if (!Array.isArray(variantIds) || !variantIds.length) return;
    if (!variantIds.some((id) => this.#isEligibleVariant(id))) return;
    this.openFor(quantity);
  }

  #handleSubmit = (event) => {
    try {
      const form = event.target.closest?.('form[action*="/cart/add"]');
      if (!form) return;

      const data = new FormData(form);
      const variantId = Number(data.get('id')) || 0;
      const quantity = Number(data.get('quantity')) || 1;

      const component = form.closest('product-form-component');
      const productId =
        Number(component?.dataset.productId || 0) ||
        Number(form.dataset.productId || 0) ||
        Number(form.querySelector('[name="product-id"]')?.value || 0);

      // Varyant VEYA ürün eşleşmesi yeterli: tema markup'ı değişse de çalışır.
      if (!this.#isEligibleVariant(variantId) && !this.#isEligibleProduct(productId)) return;

      this.openFor(quantity);
    } catch (error) {
      /* yakalayamazsak tema kendi akışını sürdürür */
    }
  };

  /**
   * Cart drawer'ın otomatik açılmasını geçici olarak kapatır. Horizon,
   * ekleme anında `auto-open` niteliğine bakar; hediye seçimi bitince geri konur.
   */
  #suppressDrawerAutoOpen() {
    try {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && drawer.hasAttribute('auto-open')) {
        this.#autoOpenSuppressed = true;
        drawer.removeAttribute('auto-open');
      }
    } catch (error) {
      /* yok say */
    }
  }

  #restoreDrawerAutoOpen() {
    try {
      if (!this.#autoOpenSuppressed) return;
      this.#autoOpenSuppressed = false;
      document.querySelector('cart-drawer-component')?.setAttribute('auto-open', '');
    } catch (error) {
      /* yok say */
    }
  }

  /* ---------- Sepet durumu ---------- */

  #isGiftItem(item) {
    return !!(item.properties && (item.properties[GIFT_PROP] || item.properties[LEGACY_GIFT_PROP]));
  }

  /** Satırın gizli bir özelliğini günceller; ürün, adet ve görünüm değişmez. */
  async #nudgeLine(item) {
    try {
      await fetch(`${ROOT()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: Object.assign({}, item.properties, { _t: String(Date.now()) }),
        }),
      });
    } catch (error) {
      console.warn('[gift-picker] satır yenilenemedi', error);
    }
  }

  /** Ürün hediye koleksiyonunda mı (örn. mini çantalar)? */
  #isGiftProduct(productId) {
    return (this.#config.gifts || []).some((gift) => Number(gift.productId) === Number(productId));
  }

  /**
   * Pop-up'tan eklenen (hediye etiketli) satır fiyatlı, butonla eklenen aynı
   * fiyatlı mini çanta 0 TL kaldıysa ikisini eşleştirir. Sepette bu iki
   * satırın fiyat alanları yer değiştirilir: hediye 0 TL, alınan ürün fiyatlı
   * görünür. Birim fiyatlar eşit olduğu için toplam değişmez.
   */
  #pairPriceSwaps(items, freeGiftKeys) {
    const pairs = [];
    const used = new Set();

    for (const gift of items) {
      if (!freeGiftKeys.has(gift.key) || gift.final_line_price === 0) continue;

      const bought = items.find(
        (item) =>
          !used.has(item.key) &&
          !this.#isGiftItem(item) &&
          this.#isGiftProduct(item.product_id) &&
          item.final_line_price === 0 &&
          item.quantity === gift.quantity &&
          item.original_line_price === gift.original_line_price
      );

      if (bought) {
        used.add(bought.key);
        pairs.push([gift.key, bought.key]);
      }
    }

    return pairs;
  }

  async #readCart(isRetry, graceUsed) {
    try {
      const cart = await fetch(`${ROOT()}cart.js`).then((response) => response.json());

      const giftItems = [];
      let eligibleQty = 0;
      let discountBudget = 0;

      for (const item of cart.items) {
        discountBudget += automaticDiscountTotal(item);

        if (this.#isGiftItem(item)) {
          giftItems.push(item);
          continue;
        }

        if (this.#eligibleIds.has(item.product_id)) eligibleQty += item.quantity;
      }

      const freeGifts = [];
      const paidGifts = [];
      let freeGiftQty = 0;

      // 1) Satır fiyatı gerçekten 0'a düşmüş hediyeler.
      for (const item of giftItems) {
        if (item.final_line_price !== 0) continue;
        discountBudget -= automaticDiscountTotal(item);
        freeGiftQty += item.quantity;
        freeGifts.push({ key: item.key, title: item.title, quantity: item.quantity });
      }

      // 2) İndirim başka satıra düşmüş olabilir (aynı üründen iki satır varken
      //    Shopify indirimi bizim satırımıza koymayabiliyor). Sepetteki otomatik
      //    indirim toplamı hediyenin tutarını karşılıyorsa hediye geçerlidir.
      for (const item of giftItems) {
        if (item.final_line_price === 0) continue;

        const price = item.original_line_price || 0;

        if (price > 0 && discountBudget >= price) {
          discountBudget -= price;
          freeGiftQty += item.quantity;
          freeGifts.push({ key: item.key, title: item.title, quantity: item.quantity });
        } else {
          paidGifts.push({ key: item.key, title: item.title });
        }
      }

      // 3) İndirim hediye yerine SATIN ALINAN mini çantaya düştüyse:
      //    Shopify eşit fiyatlı satırlarda indirimi ilk eklenen satıra veriyor.
      //    Satın alınan satırı gizli bir özellikle "yenileyince" indirim hediyeye
      //    geçiyor (preview'da 1 alınan + 1 hediye senaryosunda her seferinde doğrulandı).
      if (this.#nudgeBudget > 0) {
        const pricedGift = giftItems.find((item) => item.final_line_price > 0);
        const freeBought = pricedGift
          ? cart.items.find(
              (item) =>
                !this.#isGiftItem(item) &&
                item.final_line_price === 0 &&
                this.#isGiftProduct(item.product_id)
            )
          : null;

        if (freeBought) {
          this.#nudgeBudget -= 1;
          await this.#nudgeLine(freeBought);
          return this.#readCart(isRetry, true);
        }
      }

      // Yeni eklenen hediyede otomatik indirim bir an gecikebiliyor.
      // Hemen silmek yerine bir kez daha bakılır.
      if (paidGifts.length && !graceUsed && Date.now() - this.#giftAddedAt < DISCOUNT_GRACE_MS) {
        console.info('[gift-picker] otomatik indirim bekleniyor…');
        await sleep(900);
        return this.#readCart(isRetry, true);
      }

      // Kampanya ürünü çıkarıldıysa sahipsiz kalan hediyeleri topla.
      const orphanGifts = [];
      let excess = freeGiftQty - eligibleQty;

      if (excess > 0) {
        for (let i = freeGifts.length - 1; i >= 0 && excess > 0; i -= 1) {
          orphanGifts.push(freeGifts[i]);
          excess -= freeGifts[i].quantity;
        }
      }

      const toRemove = paidGifts.concat(orphanGifts);

      if (toRemove.length && !isRetry) {
        if (paidGifts.length) {
          console.warn(
            '[gift-picker] hediye karşılanmadı — otomatik indirim ("Satın al X kazan Y") sepete uygulanmadı.',
            'Kaldırılan:', paidGifts.map((g) => g.title).join(', ')
          );
        }
        if (orphanGifts.length) {
          console.warn('[gift-picker] sahipsiz hediye kaldırılıyor:', orphanGifts.map((g) => g.title).join(', '));
        }

        for (const gift of toRemove) {
          await fetch(`${ROOT()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: gift.key, quantity: 0 }),
          });
        }

        if (paidGifts.length) {
          this.#limitReached = true;
          this.#limitAtQty = eligibleQty;
        }

        const state = await this.#readCart(true, true);
        await this.#refreshCart();
        return state;
      }

      this.#giftsFree = freeGiftQty > 0;
      // HEDİYE çerçevesi her zaman pop-up'tan eklenen satırda (en son eklenen).
      const freeGiftKeys = new Set(freeGifts.map((gift) => gift.key));
      this.#giftLineKeys = Array.from(freeGiftKeys);
      this.#priceSwaps = this.#pairPriceSwaps(cart.items, freeGiftKeys);
      this.#eligibleQty = eligibleQty;
      this.#giftQty = freeGiftQty;
      this.#lastState = Date.now();

      if (eligibleQty < this.#limitAtQty) this.#limitAtQty = eligibleQty;

      return { eligibleQty, giftQty: freeGiftQty };
    } catch (error) {
      console.warn('[gift-picker] cart read error', error);
      return null;
    }
  }

  /** Sepette değişiklik olduğunda sahipsiz/karşılanmamış hediyeleri temizler. */
  async syncCart() {
    if (this.busy) return;

    this.#checking = true;
    await this.#readCart();
    this.#checking = false;

    this.markGiftLines();
  }

  /**
   * Formu/isteği yakalayamadığımız durumlar için yedek yol: ürün sepete
   * gittikten sonra hediye hakkı varsa pop-up'ı açar.
   */
  async maybeOpen() {
    try {
      if (this.busy) return;
      if (!this.#config.gifts || !this.#config.gifts.length) return;
      if (this.dialog && this.dialog.open) return;

      this.#checking = true;
      const state = await this.#readCart();
      this.#checking = false;

      if (!state) return;

      if (this.#limitReached) {
        if (state.eligibleQty <= this.#limitAtQty) return;
        this.#limitReached = false;
      }

      if (state.eligibleQty <= state.giftQty) return;

      this.#closeDrawer();
      this.#showAddError('');
      if (this.dialog && !this.dialog.open) this.dialog.showModal();
    } catch (error) {
      this.#checking = false;
      console.warn('[gift-picker] maybeOpen error', error);
    }
  }

  /* ---------- Etkileşim ---------- */

  #handleClick = (event) => {
    const option = event.target.closest('[data-gift-option]');
    if (option) {
      this.#select(option);
      return;
    }

    if (event.target.closest('[data-gift-submit]')) {
      if (!this.#variantChosen()) {
        this.#showVariantError(true);
        this.variantSelect.focus();
        return;
      }
      this.#finish(true);
      return;
    }

    if (event.target.closest('[data-gift-skip]') || event.target.closest('[data-gift-close]')) {
      this.#finish(false);
    }
  };

  #select(option) {
    for (const el of this.querySelectorAll('[data-gift-option]')) {
      el.classList.toggle('is-selected', el === option);
      el.setAttribute('aria-checked', el === option ? 'true' : 'false');
    }

    this.#selected = option.dataset.variantId;
    this.#fillVariants(Number(option.dataset.productId || 0));
    this.#showAddError('');

    if (this.mainImage && option.dataset.image) {
      this.mainImage.src = option.dataset.image;
      this.mainImage.alt = option.dataset.title || '';
    }

    if (this.titleEl) this.titleEl.textContent = option.dataset.title || '';
    if (this.compareEl) this.compareEl.textContent = option.dataset.price || '';
  }

  /** Seçilen hediye ürününün varyantlarıyla (örn. askı rengi) dropdown'ı doldurur. */
  #fillVariants(productId) {
    if (!this.variantSelect || !this.variantField) return;

    const gift = (this.#config.gifts || []).find((item) => item.productId === productId);
    const variants = (gift && gift.variants) || [];

    // Tek varyantlı üründe dropdown gösterilmez.
    if (variants.length < 2) {
      this.variantField.hidden = true;
      this.variantSelect.innerHTML = '';
      this.#showVariantError(false);
      return;
    }

    // Ürünün option adından bağımsız sabit metinler:
    // başlık "ASKI RENGİ", dropdown placeholder'ı "SEÇİNİZ".
    const labelText = OPTION_LABEL.toLocaleUpperCase('tr-TR');
    const placeholderText = 'SEÇİNİZ';

    if (this.variantLabel) {
      this.variantLabel.textContent = labelText;
    }

    this.variantSelect.innerHTML = '';

    // Varsayılan seçim yok: müşteri bilinçli olarak seçmek zorunda.
    const placeholderEl = document.createElement('option');
    placeholderEl.value = '';
    placeholderEl.textContent = placeholderText;
    placeholderEl.disabled = true;
    placeholderEl.selected = true;
    this.variantSelect.appendChild(placeholderEl);

    for (const variant of variants) {
      const optionEl = document.createElement('option');
      optionEl.value = String(variant.id);
      optionEl.textContent = variant.available ? variant.title : `${variant.title} — tükendi`;
      optionEl.disabled = !variant.available;
      this.variantSelect.appendChild(optionEl);
    }

    this.variantSelect.value = '';
    this.#selected = null;

    this.variantField.hidden = false;
    this.#showVariantError(false);
  }

  /** Dropdown görünürken seçim yapılmış mı? */
  #variantChosen() {
    if (this.variantField && !this.variantField.hidden && this.variantSelect) {
      return !!this.variantSelect.value;
    }
    return !!this.#selected;
  }

  #showVariantError(show) {
    if (this.variantError) this.variantError.hidden = !show;
    if (this.variantSelect) this.variantSelect.classList.toggle('is-invalid', !!show);
  }

  /** Hediye eklenemediğinde müşteriye görünür hata. Boş metin gizler. */
  #showAddError(message) {
    if (!this.addError) return;
    this.addError.textContent = message || '';
    this.addError.hidden = !message;
  }

  /** Eklenecek varyant: dropdown varsa oradaki seçim, yoksa ürünün varsayılanı. */
  #variantToAdd() {
    if (this.variantField && !this.variantField.hidden && this.variantSelect && this.variantSelect.value) {
      return Number(this.variantSelect.value);
    }
    return Number(this.#selected);
  }

  #resetSubmit() {
    if (this.submitButton) this.submitButton.disabled = false;
    if (this.submitText) this.submitText.textContent = 'HEDİYEMİ EKLE';
  }

  /**
   * Pop-up'ı kapatır ve seçilen hediyeyi sepete ekler.
   * Ekleme başarısız olursa pop-up KAPANMAZ, hata gösterilir.
   */
  async #finish(withGift) {
    if (this.#adding) return;

    this.#adding = true;

    if (withGift && this.#selected) {
      if (this.submitButton) this.submitButton.disabled = true;
      if (this.submitText) this.submitText.textContent = 'EKLENİYOR…';
      this.#showAddError('');

      const variantId = this.#variantToAdd();

      if (!variantId) {
        console.warn('[gift-picker] geçersiz hediye varyantı');
        this.#showAddError('Hediye seçimi okunamadı, lütfen tekrar deneyin.');
        this.#resetSubmit();
        this.#adding = false;
        return;
      }

      try {
        const response = await fetch(`${ROOT()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            // _g: aynı hediye iki kez seçilirse tek satırda birleşmesin.
            items: [{ id: variantId, quantity: 1, properties: { [GIFT_PROP]: 'Ücretsiz', _g: String(Date.now()) } }],
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          console.error('[gift-picker] hediye eklenemedi —', response.status, data);

          this.#showAddError(data.description || data.message || 'Hediye sepete eklenemedi. Lütfen tekrar deneyin.');
          this.#resetSubmit();
          this.#adding = false;
          return;
        }

        this.#giftAddedAt = Date.now();
        this.#nudgeBudget = 2;
      } catch (error) {
        console.error('[gift-picker] gift add error', error);
        this.#showAddError('Bağlantı hatası, hediye eklenemedi.');
        this.#resetSubmit();
        this.#adding = false;
        return;
      }
    }

    try {
      await this.#readCart();
      await this.#refreshCart();
    } catch (error) {
      /* yok say */
    }

    if (!withGift) {
      // Hediye reddedildi: sepete yeni uygun ürün eklenene kadar tekrar sorma.
      this.#limitReached = true;
      this.#limitAtQty = this.#eligibleQty;
    }

    this.#resetSubmit();
    this.#adding = false;

    if (this.dialog && this.dialog.open) this.dialog.close();

    // Drawer yalnızca burada, hediye akışı bittikten sonra açılır.
    this.#restoreDrawerAutoOpen();
    this.#openDrawer();
  }

  /* ---------- Sepet görünümü ---------- */

  async #refreshCart() {
    try {
      const target = document.querySelector('cart-items-component[data-section-id]');
      if (!target) return;

      const sectionId = target.dataset.sectionId;
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
      const html = await fetch(url).then((response) => response.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const fresh = doc.querySelector('cart-items-component');
      if (fresh) target.innerHTML = fresh.innerHTML;

      const freshDialog = doc.querySelector('.cart-drawer__dialog');
      const currentDialog = document.querySelector('.cart-drawer__dialog');
      if (freshDialog && currentDialog) {
        currentDialog.classList.toggle('cart-drawer--empty', freshDialog.classList.contains('cart-drawer--empty'));
      }

      const freshIcon = doc.querySelector('cart-icon-component');
      if (freshIcon) {
        for (const icon of document.querySelectorAll('cart-icon-component')) {
          icon.innerHTML = freshIcon.innerHTML;
        }
      }

      this.markGiftLines();
    } catch (error) {
      console.warn('[gift-picker] refresh error', error);
    }
  }

  /**
   * "HEDİYE" çerçevesini, indirimin gerçekten düştüğü (0 TL) satırlara çizer.
   * Satırlar sepet anahtarıyla (data-key) bulunur; drawer ve /cart sayfası aynı.
   */
  markGiftLines() {
    try {
      const keys = this.#giftsFree ? new Set(this.#giftLineKeys) : new Set();

      for (const line of document.querySelectorAll('.gift-line')) {
        if (keys.has(line.dataset.key)) continue;
        line.classList.remove('gift-line');
        line.querySelector(':scope > .gift-line__badge')?.remove();
      }

      for (const line of document.querySelectorAll('cart-items-component [data-key]')) {
        if (!keys.has(line.dataset.key) || line.classList.contains('gift-line')) continue;

        line.classList.add('gift-line');

        const badge = document.createElement('span');
        badge.className = 'gift-line__badge';
        badge.textContent = 'Hediye';
        line.prepend(badge);
      }
    } catch (error) {
      /* yok say */
    }
  }

  #watchCart() {
    const observer = new MutationObserver(() => {
      clearTimeout(this.__markTimer);
      this.__markTimer = setTimeout(async () => {
        if (Date.now() - this.#lastState > 4000 && !this.busy) {
          this.#checking = true;
          await this.#readCart();
          this.#checking = false;
        }
        this.markGiftLines();
      }, 120);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- Drawer ---------- */

  #closeDrawer() {
    try {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.closeDialog === 'function') drawer.closeDialog();
    } catch (error) {
      /* yok say */
    }
  }

  /** Hızlı ekleme modalı açıksa kapatır (hediye pop-up'ı tek başına kalsın). */
  #closeQuickAdd() {
    try {
      const quickAdd = document.getElementById('quick-add-dialog');
      if (quickAdd && typeof quickAdd.closeDialog === 'function') quickAdd.closeDialog();

      // Kart üzerindeki hızlı ekleme butonunun "görünür kal" durumunu bırak.
      for (const el of document.querySelectorAll('quick-add[stay-visible], .quick-add[stay-visible]')) {
        el.removeAttribute('stay-visible');
      }
    } catch (error) {
      /* yok say */
    }
  }

  #openDrawer() {
    try {
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer) return;

      if (typeof drawer.showDialog === 'function') {
        drawer.showDialog();
        return;
      }

      const trigger = drawer.querySelector('[data-testid="cart-drawer-trigger"]');
      if (trigger) trigger.click();
    } catch (error) {
      /* yok say */
    }
  }
}

/* ---------- /cart/add isteklerini dinleme ---------- */

/** İstek gövdesinden varyant ID'lerini ve adedi çıkarır (JSON / FormData / query string). */
function readAddPayload(args) {
  const ids = [];
  let quantity = 1;

  try {
    const init = args[1];
    const input = args[0];
    const body = (init && init.body) || (input && typeof input !== 'string' ? input.body : null);
    if (!body) return { ids, quantity };

    const collect = (id, qty) => {
      const numeric = Number(id);
      if (numeric) ids.push(numeric);
      const q = Number(qty);
      if (q) quantity = Math.max(quantity, q);
    };

    if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        if (Array.isArray(data.items)) {
          for (const item of data.items) collect(item.id, item.quantity);
        } else {
          collect(data.id, data.quantity);
        }
      } catch (error) {
        const params = new URLSearchParams(body);
        collect(params.get('id'), params.get('quantity'));
      }
    } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
      collect(body.get('id'), body.get('quantity'));
      let index = 0;
      while (body.has(`items[${index}][id]`)) {
        collect(body.get(`items[${index}][id]`), body.get(`items[${index}][quantity]`));
        index += 1;
      }
    } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      collect(body.get('id'), body.get('quantity'));
    }
  } catch (error) {
    /* yok say */
  }

  return { ids, quantity };
}

let hookInstalled = false;

function installCartHook() {
  if (hookInstalled) return;
  hookInstalled = true;

  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    let isAdd = false;

    try {
      const input = args[0];
      const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      isAdd = !!url && url.includes('/cart/add');

      // Pop-up isteğin YANITI beklenmeden, gönderim anında açılır.
      if (isAdd) {
        const picker = window.__giftPicker;
        if (picker && !picker.busy) {
          const payload = readAddPayload(args);
          picker.notifyAdd(payload.ids, payload.quantity);
        }
      }
    } catch (error) {
      /* yok say */
    }

    const result = originalFetch.apply(this, args);

    try {
      const input = args[0];
      const url = typeof input === 'string' ? input : input && input.url ? input.url : '';

      if (isAdd) {
        result
          .then(() => {
            setTimeout(() => {
              const picker = window.__giftPicker;
              if (picker && !picker.busy) picker.maybeOpen();
            }, 120);
          })
          .catch(() => {});
      } else if (url && (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'))) {
        result
          .then(() => {
            setTimeout(() => {
              const picker = window.__giftPicker;
              if (picker) picker.syncCart();
            }, 200);
          })
          .catch(() => {});
      }
    } catch (error) {
      /* yok say */
    }

    return result;
  };
}

if (!customElements.get('gift-picker-component')) {
  customElements.define('gift-picker-component', GiftPickerComponent);
}